import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Save, ShieldCheck, Check, ChevronRight } from 'lucide-react';

interface Section {
  code: string;
  name: string;
  group?: string;
  description?: string;
  enabled: boolean;
  sum_insured_zar?: number | null;
  premium_zar?: number | null;
  deductible_zar?: number | null;
  indemnity_period_months?: number | null;
  notes?: string;
}

interface ClientOpt {
  id: string;
  name: string;
  trading_name?: string;
  broker_id?: string;
  broker_name?: string | null;
}

interface SiteOpt {
  id: string;
  name: string;
  site_reference?: string;
  client_id?: string;
  city?: string;
}

interface SiteOverride { sum_insured_zar?: number | null; excess_zar?: number | null; premium_zar?: number | null; }

interface CoverForm {
  client_id: string;
  site_id: string;
  insured_name: string;
  policy_period_start: string;
  policy_period_end: string;
  sections: Section[];
  endorsements: string[];
  warranties: string[];
  notes: string;
  site_overrides?: Record<string, SiteOverride>;
}

const emptyForm: CoverForm = {
  client_id: '', site_id: '', insured_name: '',
  policy_period_start: '', policy_period_end: '',
  sections: [], endorsements: [], warranties: [], notes: '',
  site_overrides: {},
};

const fmtZAR = (n?: number | null): string => {
  if (n == null || Number.isNaN(n)) return 'R\u00a00';
  return `R\u00a0${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const CoverBuilderPage: React.FC = () => {
  const { coverId } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const isEdit = Boolean(coverId);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<CoverForm>(emptyForm);
  const [clients, setClients] = useState<ClientOpt[]>([]);
  const [sites, setSites] = useState<SiteOpt[]>([]);
  const [saving, setSaving] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [endorseInput, setEndorseInput] = useState('');
  const [warrantyInput, setWarrantyInput] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [cs, cats] = await Promise.all([
          api.get<ClientOpt[]>('/clients'),
          api.get<{ sections: Section[] }>('/covers/catalog'),
        ]);
        setClients(cs.data);
        if (isEdit && coverId) {
          const r = await api.get<CoverForm & { id: string }>(`/covers/${coverId}`);
          setForm({
            client_id: r.data.client_id || '',
            site_id: r.data.site_id || '',
            insured_name: r.data.insured_name || '',
            policy_period_start: (r.data.policy_period_start || '').slice(0, 10),
            policy_period_end: (r.data.policy_period_end || '').slice(0, 10),
            sections: r.data.sections?.length ? r.data.sections : cats.data.sections,
            endorsements: r.data.endorsements || [],
            warranties: r.data.warranties || [],
            notes: r.data.notes || '',
          });
        } else {
          // Prefill from ?client_id / ?site_id / ?insured_name query params
          const preClient = params.get('client_id') || '';
          const preSite = params.get('site_id') || '';
          const preInsured = params.get('insured_name') || '';
          setForm({ ...emptyForm, client_id: preClient, site_id: preSite, insured_name: preInsured, sections: cats.data.sections });
        }
      } catch {
        toast.error('Failed to load builder data');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverId]);

  // When client changes, refresh site options for that client and reset the
  // selection unless it still belongs to the chosen client.
  useEffect(() => {
    if (!form.client_id) { setSites([]); return; }
    void (async () => {
      try {
        const r = await api.get<SiteOpt[]>('/sites', { params: { client_id: form.client_id } });
        setSites(r.data);
        if (form.site_id && !r.data.some((s) => s.id === form.site_id)) {
          setForm((f) => ({ ...f, site_id: '' }));
        }
        // Auto-fill insured name from client if blank
        if (!form.insured_name) {
          const c = clients.find((x) => x.id === form.client_id);
          if (c) setForm((f) => ({ ...f, insured_name: c.trading_name || c.name }));
        }
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.client_id]);

  const totals = useMemo(() => {
    const enabled = form.sections.filter((s) => s.enabled);
    const si = enabled.reduce((s, x) => s + Number(x.sum_insured_zar || 0), 0);
    const prem = enabled.reduce((s, x) => s + Number(x.premium_zar || 0), 0);
    return { count: enabled.length, si, prem };
  }, [form.sections]);

  const grouped = useMemo(() => {
    const g: Record<string, Section[]> = {};
    form.sections.forEach((s) => {
      const grp = s.group || 'Other';
      g[grp] = g[grp] || [];
      g[grp].push(s);
    });
    return g;
  }, [form.sections]);

  const canProceed1 = form.client_id && form.insured_name.trim();
  const canProceed2 = form.sections.some((s) => s.enabled);
  const canProceed3 = form.policy_period_start && form.policy_period_end;

  const patchSection = (code: string, patch: Partial<Section>) => {
    setForm((f) => ({ ...f, sections: f.sections.map((s) => s.code === code ? { ...s, ...patch } : s) }));
  };

  const save = async (goToDetail = false): Promise<string | null> => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        policy_period_start: form.policy_period_start || null,
        policy_period_end: form.policy_period_end || null,
      };
      if (isEdit && coverId) {
        await api.patch(`/covers/${coverId}`, payload);
        toast.success('Draft saved');
        if (goToDetail) nav(`/insurer/covers/${coverId}`);
        return coverId;
      } else {
        const r = await api.post<{ id: string }>('/covers', payload);
        toast.success('Draft created');
        if (goToDetail) nav(`/insurer/covers/${r.data.id}`);
        else nav(`/insurer/covers/${r.data.id}/edit`, { replace: true });
        return r.data.id;
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Save failed';
      toast.error(msg);
      return null;
    } finally { setSaving(false); }
  };

  const issue = async () => {
    let id = coverId;
    if (!id) {
      const savedId = await save(false);
      if (!savedId) return;
      id = savedId;
    } else {
      const ok = await save(false);
      if (!ok) return;
    }
    setIssuing(true);
    try {
      await api.post(`/covers/${id}/issue`);
      toast.success('Confirmation of Cover issued');
      nav(`/insurer/covers/${id}`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Issue failed';
      toast.error(msg);
    } finally { setIssuing(false); }
  };

  return (
    <div className="space-y-4 animate-fade-in max-w-6xl" data-testid="cover-builder-page">
      <button type="button" onClick={() => nav('/insurer/covers')} className="text-xs text-raptor-navy/60 hover:text-raptor-blue inline-flex items-center gap-1">
        <ArrowLeft size={12} /> Confirmations of Cover
      </button>
      <div>
        <h2 className="font-headline text-3xl text-raptor-navy tracking-wide">{isEdit ? 'Edit confirmation of cover' : 'New confirmation of cover'}</h2>
        <p className="text-sm text-raptor-navy/60 font-body">Step {step} of 4 · A draft is saved as you progress; issue at the end to lock the record and produce a PDF.</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-1 flex-wrap" data-testid="cover-stepper">
        {['Insured & site', 'Sections of cover', 'Period & pricing', 'Review & issue'].map((label, i) => {
          const n = i + 1;
          const active = step === n;
          const done = step > n;
          return (
            <button
              key={label}
              type="button"
              onClick={() => setStep(n)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-body border transition-colors ${
                active ? 'bg-raptor-blue text-white border-raptor-blue' :
                done ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' :
                'bg-white text-raptor-navy/70 border-border/60 hover:bg-raptor-neutral/40'
              }`}
            >
              <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px] font-bold ${active ? 'bg-white text-raptor-blue' : done ? 'bg-emerald-500 text-white' : 'bg-raptor-neutral/60 text-raptor-navy/60'}`}>
                {done ? <Check size={10} /> : n}
              </span>
              {label}
            </button>
          );
        })}
      </div>

      {/* Step 1: Insured & Site */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Who is covered?</CardTitle>
            <CardDescription>Pick the client and (optionally) a specific site. The insured name defaults to the client's trading name.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Client *</Label>
                <Select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="cover-client-select">
                  <option value="">— Select a client —</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.trading_name || c.name}{c.broker_name ? ` · ${c.broker_name}` : ''}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label>Site <span className="text-raptor-navy/50 text-xs font-normal">(optional — blank means portfolio-wide)</span></Label>
                <Select value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} disabled={!form.client_id} data-testid="cover-site-select">
                  <option value="">— No specific site —</option>
                  {sites.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}{s.site_reference ? ` (${s.site_reference})` : ''}{s.city ? ` · ${s.city}` : ''}</option>
                  ))}
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Insured name *</Label>
                <Input value={form.insured_name} onChange={(e) => setForm({ ...form, insured_name: e.target.value })} placeholder="As it should appear on the schedule" data-testid="cover-insured-name" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => nav('/insurer/covers')}>Cancel</Button>
              <Button variant="gradient" disabled={!canProceed1} onClick={() => setStep(2)} data-testid="cover-next-1">
                Next: Sections of cover <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Sections */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Sections of cover</CardTitle>
            <CardDescription>Toggle the perils this cover applies to and set sums insured / deductibles / premium. Grouped by cover class.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.keys(grouped).map((grp) => (
              <div key={grp}>
                <div className="text-[11px] uppercase tracking-widest text-raptor-blue font-body font-bold mb-2">{grp}</div>
                <div className="space-y-2">
                  {grouped[grp].map((s) => (
                    <div key={s.code} className={`border rounded-md transition-colors ${s.enabled ? 'border-raptor-blue/40 bg-raptor-blue/[0.03]' : 'border-border/60 bg-white'}`}>
                      <div className="p-3 flex items-start gap-3">
                        <label className="flex items-center gap-2 cursor-pointer pt-0.5">
                          <input
                            type="checkbox"
                            checked={s.enabled}
                            onChange={(e) => patchSection(s.code, { enabled: e.target.checked })}
                            className="w-4 h-4 accent-raptor-blue"
                            data-testid={`cover-section-toggle-${s.code}`}
                          />
                        </label>
                        <div className="flex-1">
                          <div className="font-semibold text-raptor-navy text-sm">{s.name}</div>
                          <div className="text-xs text-raptor-navy/60 font-body">{s.description}</div>
                          {s.enabled && (
                            <div className="grid md:grid-cols-4 gap-2 mt-3">
                              <div>
                                <Label className="text-[10px]">Sum insured (ZAR)</Label>
                                <Input
                                  type="number" step="0.01" min="0"
                                  value={s.sum_insured_zar ?? ''}
                                  onChange={(e) => patchSection(s.code, { sum_insured_zar: e.target.value === '' ? null : Number(e.target.value) })}
                                  data-testid={`cover-si-${s.code}`}
                                />
                              </div>
                              <div>
                                <Label className="text-[10px]">Deductible (ZAR)</Label>
                                <Input
                                  type="number" step="0.01" min="0"
                                  value={s.deductible_zar ?? ''}
                                  onChange={(e) => patchSection(s.code, { deductible_zar: e.target.value === '' ? null : Number(e.target.value) })}
                                />
                              </div>
                              <div>
                                <Label className="text-[10px]">Premium (ZAR)</Label>
                                <Input
                                  type="number" step="0.01" min="0"
                                  value={s.premium_zar ?? ''}
                                  onChange={(e) => patchSection(s.code, { premium_zar: e.target.value === '' ? null : Number(e.target.value) })}
                                  data-testid={`cover-prem-${s.code}`}
                                />
                              </div>
                              {s.code === 'business_interruption' && (
                                <div>
                                  <Label className="text-[10px]">Indemnity period (mo.)</Label>
                                  <Input
                                    type="number" step="1" min="0"
                                    value={s.indemnity_period_months ?? 12}
                                    onChange={(e) => patchSection(s.code, { indemnity_period_months: e.target.value === '' ? null : Number(e.target.value) })}
                                  />
                                </div>
                              )}
                              <div className="md:col-span-4">
                                <Label className="text-[10px]">Notes</Label>
                                <Input
                                  value={s.notes ?? ''}
                                  onChange={(e) => patchSection(s.code, { notes: e.target.value })}
                                  placeholder="Optional — e.g. specific limits, sub-limits, extensions"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex items-center justify-between gap-2 flex-wrap pt-2">
              <div className="text-xs text-raptor-navy/70 font-body">
                {totals.count} section{totals.count === 1 ? '' : 's'} enabled · SI {fmtZAR(totals.si)} · Premium {fmtZAR(totals.prem)}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft size={14} className="mr-1" /> Back</Button>
                <Button variant="gradient" disabled={!canProceed2} onClick={() => setStep(3)} data-testid="cover-next-2">
                  Next: Period &amp; pricing <ArrowRight size={14} className="ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Period, endorsements, warranties, notes */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Policy period &amp; conditions</CardTitle>
            <CardDescription>Set the cover window and capture any endorsements, warranties or notes.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Period start *</Label>
                <Input type="date" value={form.policy_period_start} onChange={(e) => setForm({ ...form, policy_period_start: e.target.value })} data-testid="cover-period-start" />
              </div>
              <div>
                <Label>Period end *</Label>
                <Input type="date" value={form.policy_period_end} onChange={(e) => setForm({ ...form, policy_period_end: e.target.value })} data-testid="cover-period-end" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Endorsements</Label>
                <div className="space-y-1 mb-2">
                  {form.endorsements.map((e, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm border rounded px-2 py-1 bg-raptor-neutral/30">
                      <span className="flex-1">{e}</span>
                      <button type="button" className="text-raptor-navy/60 hover:text-red-600 text-xs" onClick={() => setForm({ ...form, endorsements: form.endorsements.filter((_, j) => j !== i) })}>Remove</button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={endorseInput} onChange={(e) => setEndorseInput(e.target.value)} placeholder="Add endorsement…" onKeyDown={(e) => { if (e.key === 'Enter' && endorseInput.trim()) { e.preventDefault(); setForm({ ...form, endorsements: [...form.endorsements, endorseInput.trim()] }); setEndorseInput(''); } }} />
                  <Button variant="outline" onClick={() => { if (endorseInput.trim()) { setForm({ ...form, endorsements: [...form.endorsements, endorseInput.trim()] }); setEndorseInput(''); } }}>Add</Button>
                </div>
              </div>
              <div>
                <Label>Warranties</Label>
                <div className="space-y-1 mb-2">
                  {form.warranties.map((w, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm border rounded px-2 py-1 bg-raptor-neutral/30">
                      <span className="flex-1">{w}</span>
                      <button type="button" className="text-raptor-navy/60 hover:text-red-600 text-xs" onClick={() => setForm({ ...form, warranties: form.warranties.filter((_, j) => j !== i) })}>Remove</button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={warrantyInput} onChange={(e) => setWarrantyInput(e.target.value)} placeholder="Add warranty…" onKeyDown={(e) => { if (e.key === 'Enter' && warrantyInput.trim()) { e.preventDefault(); setForm({ ...form, warranties: [...form.warranties, warrantyInput.trim()] }); setWarrantyInput(''); } }} />
                  <Button variant="outline" onClick={() => { if (warrantyInput.trim()) { setForm({ ...form, warranties: [...form.warranties, warrantyInput.trim()] }); setWarrantyInput(''); } }}>Add</Button>
                </div>
              </div>
            </div>

            <div>
              <Label>Additional notes</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any additional information to include on the schedule…" />
            </div>

            {/* Per-site overrides — only shows when portfolio-wide (no specific site) so multi-site clients can have per-address limits */}
            {!form.site_id && form.client_id && sites.length > 0 && (
              <div className="border border-border/70 rounded-md p-3" data-testid="site-overrides-block">
                <Label>Per-site overrides <span className="text-raptor-navy/50 text-xs font-normal">(optional — set specific Sum Insured, Excess and Premium per site)</span></Label>
                <p className="text-xs text-raptor-navy/60 mb-2 font-body">Any site left blank uses the section totals above. Overrides render on the policy schedule&apos;s risk-address table.</p>
                <div className="space-y-2">
                  {sites.map((s) => {
                    const ov = (form.site_overrides || {})[s.id] || {};
                    const patch = (p: Partial<SiteOverride>) => setForm((f) => ({ ...f, site_overrides: { ...(f.site_overrides || {}), [s.id]: { ...ov, ...p } } }));
                    return (
                      <div key={s.id} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end" data-testid={`site-override-${s.id}`}>
                        <div className="md:col-span-1 text-sm text-raptor-navy">
                          <div className="font-semibold">{s.name}</div>
                          <div className="text-xs text-raptor-navy/60 font-tabular">{s.site_reference || '—'}</div>
                        </div>
                        <div>
                          <Label className="text-[10px]">Sum insured (R)</Label>
                          <Input type="number" step="0.01" value={ov.sum_insured_zar ?? ''} onChange={(e) => patch({ sum_insured_zar: e.target.value === '' ? null : Number(e.target.value) })} />
                        </div>
                        <div>
                          <Label className="text-[10px]">Excess (R)</Label>
                          <Input type="number" step="0.01" value={ov.excess_zar ?? ''} onChange={(e) => patch({ excess_zar: e.target.value === '' ? null : Number(e.target.value) })} />
                        </div>
                        <div>
                          <Label className="text-[10px]">Premium (R)</Label>
                          <Input type="number" step="0.01" value={ov.premium_zar ?? ''} onChange={(e) => patch({ premium_zar: e.target.value === '' ? null : Number(e.target.value) })} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft size={14} className="mr-1" /> Back</Button>
              <Button variant="gradient" disabled={!canProceed3} onClick={() => setStep(4)} data-testid="cover-next-3">
                Next: Review &amp; issue <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Review & issue */}
      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Review &amp; issue</CardTitle>
            <CardDescription>Save as draft to keep editing, or issue to lock the record and generate a PDF.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-3">
              <ReviewCell label="Insured" value={form.insured_name} />
              <ReviewCell label="Client" value={clients.find((c) => c.id === form.client_id)?.name || '—'} />
              <ReviewCell label="Site" value={sites.find((s) => s.id === form.site_id)?.name || 'Portfolio-wide'} />
              <ReviewCell label="Period" value={`${form.policy_period_start || '—'} → ${form.policy_period_end || '—'}`} />
              <ReviewCell label="Sections enabled" value={String(totals.count)} />
              <ReviewCell label="Endorsements / Warranties" value={`${form.endorsements.length} / ${form.warranties.length}`} />
              <ReviewCell label="Total sum insured" value={fmtZAR(totals.si)} accent />
              <ReviewCell label="Total premium" value={fmtZAR(totals.prem)} accent />
              <div />
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-raptor-navy/60 font-body mb-1">Enabled sections</div>
              <div className="flex flex-wrap gap-1">
                {form.sections.filter((s) => s.enabled).length === 0 && <span className="text-sm text-raptor-navy/50 italic">None</span>}
                {form.sections.filter((s) => s.enabled).map((s) => (
                  <Badge key={s.code} variant="blue">{s.name} · {fmtZAR(s.sum_insured_zar || 0)}</Badge>
                ))}
              </div>
            </div>

            <div className="flex justify-between gap-2 pt-2 flex-wrap">
              <Button variant="outline" onClick={() => setStep(3)}><ArrowLeft size={14} className="mr-1" /> Back</Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => void save(true)} disabled={saving} data-testid="cover-save-draft">
                  <Save size={14} className="mr-1" /> {saving ? 'Saving…' : 'Save draft'}
                </Button>
                <Button variant="gradient" onClick={() => void issue()} disabled={issuing || saving} data-testid="cover-issue-btn">
                  <ShieldCheck size={14} className="mr-1" /> {issuing ? 'Issuing…' : 'Issue confirmation of cover'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const ReviewCell: React.FC<{ label: string; value: string; accent?: boolean }> = ({ label, value, accent }) => (
  <div className={`rounded-md border p-3 ${accent ? 'border-raptor-blue/40 bg-raptor-blue/[0.04]' : 'border-border/60 bg-white'}`}>
    <div className="text-[10px] uppercase tracking-widest text-raptor-navy/60 font-body">{label}</div>
    <div className={`font-headline tracking-wide ${accent ? 'text-raptor-blue text-lg' : 'text-raptor-navy text-base'}`}>{value || '—'}</div>
  </div>
);

export default CoverBuilderPage;
